(() => {
  if (window.KalmixGroundTruthScenarioResults) {
    return;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function formatTime(seconds) {
    const value = Math.max(0, Math.floor(Number(seconds) || 0));
    const minutes = Math.floor(value / 60);
    const remainder = value % 60;

    return `${minutes}:${String(remainder).padStart(2, "0")}`;
  }

  function formatMeters(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number.toFixed(3) : "—";
  }

  function formatPercent(value) {
    const number = Number(value);

    return Number.isFinite(number)
      ? `${number.toFixed(3).replace(/\.?0+$/, "")}%`
      : "—";
  }

  function formatEpochs(value) {
    const number = Number(value);

    return Number.isFinite(number)
      ? new Intl.NumberFormat("en-US").format(number)
      : "—";
  }

  function positiveNumber(value, fallback) {
    const number = Number(value);

    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function initializeNumberFormats(section) {
    section.querySelectorAll("[data-gt-scenario-meter]").forEach((element) => {
      element.textContent = formatMeters(element.dataset.gtScenarioMeter);
    });

    section.querySelectorAll("[data-gt-scenario-epochs]").forEach((element) => {
      element.textContent = formatEpochs(element.dataset.gtScenarioEpochs);
    });

    section.querySelectorAll("[data-gt-scenario-distance]").forEach((element) => {
      const value = Number(element.dataset.gtScenarioDistance);

      if (Number.isFinite(value)) {
        element.textContent = `${value.toFixed(1)} km`;
      }
    });

    section.querySelectorAll("[data-gt-scenario-detail-epochs]").forEach((element) => {
      element.textContent = `${formatEpochs(element.dataset.gtScenarioDetailEpochs)} ep`;
    });

    section.querySelectorAll("[data-gt-scenario-detail-percent]").forEach((element) => {
      element.textContent = formatPercent(element.dataset.gtScenarioDetailPercent);
    });
  }

  function initializeScenarioNavigation(section) {
    const navigation = section.querySelector(".gt-scenario-nav");

    if (!navigation || navigation.dataset.initialized === "true") {
      return;
    }

    navigation.dataset.initialized = "true";

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    navigation.querySelectorAll('a[href^="#scenario-"]').forEach((link) => {
      link.addEventListener("click", (event) => {
        const href = link.getAttribute("href");

        if (!href || href.length < 2) {
          return;
        }

        const target = document.getElementById(href.slice(1));

        if (!target) {
          return;
        }

        event.preventDefault();

        target.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "start"
        });

        if (window.history && window.history.pushState) {
          window.history.pushState(null, "", href);
        }
      });
    });
  }

  function initializeDistributionTooltips(section) {
    section.querySelectorAll("[data-gt-scenario-distribution]").forEach((distribution) => {
      if (distribution.dataset.initialized === "true") {
        return;
      }

      distribution.dataset.initialized = "true";

      const tooltip = distribution.querySelector("[data-gt-scenario-tooltip]");
      const segments = distribution.querySelectorAll("[data-gt-scenario-segment]");

      function hideTooltip() {
        tooltip.classList.remove("is-visible");
      }

      function showTooltip(segment) {
        const title = segment.dataset.label || "";
        const kind = segment.dataset.kind || "";
        const percent = segment.dataset.percent || "";
        const epochs = segment.dataset.epochs || "";

        tooltip.innerHTML = `
          <div class="gt-scenario__tooltip-kind">${escapeHtml(kind)}</div>
          <div class="gt-scenario__tooltip-title">${escapeHtml(title)}</div>
          <div class="gt-scenario__tooltip-meta">
            <span>${escapeHtml(formatPercent(percent))}</span>
            <span>${escapeHtml(formatEpochs(epochs))} ep</span>
          </div>
        `;

        const parentRect = distribution.getBoundingClientRect();
        const segmentRect = segment.getBoundingClientRect();
        const tooltipWidth = tooltip.offsetWidth || 175;
        const tooltipHeight = tooltip.offsetHeight || 70;

        let left =
          segmentRect.left -
          parentRect.left +
          segmentRect.width / 2 -
          tooltipWidth / 2;

        let top = segmentRect.top - parentRect.top - tooltipHeight - 10;

        left = Math.max(
          8,
          Math.min(left, distribution.clientWidth - tooltipWidth - 8)
        );

        if (top < 8) {
          top = segmentRect.bottom - parentRect.top + 10;
        }

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.classList.add("is-visible");
      }

      segments.forEach((segment) => {
        segment.addEventListener("pointerenter", () => showTooltip(segment));
        segment.addEventListener("pointerleave", hideTooltip);
        segment.addEventListener("focus", () => showTooltip(segment));
        segment.addEventListener("blur", hideTooltip);
      });
    });
  }

  function initializePlayer(player) {
    if (!player || player.dataset.initialized === "true") {
      return;
    }

    player.dataset.initialized = "true";

    const video = player.querySelector("[data-gt-scenario-video]");
    const toggleButton = player.querySelector("[data-gt-video-toggle]");
    const bigPlayButton = player.querySelector("[data-gt-video-big-play]");
    const replayButton = player.querySelector("[data-gt-video-replay]");
    const fullscreenButton = player.querySelector("[data-gt-video-fullscreen]");
    const progress = player.querySelector("[data-gt-video-progress]");
    const timeLabel = player.querySelector("[data-gt-video-time]");
    const speedButtons = Array.from(
      player.querySelectorAll("[data-gt-video-speed-option]")
    );

    if (!video) {
      return;
    }

    const nativeSpeed = positiveNumber(player.dataset.videoNativeSpeed, 8);
    const defaultSpeed = positiveNumber(
      player.dataset.videoDefaultSpeed,
      nativeSpeed
    );

    function getDisplayedSpeed(button) {
      return positiveNumber(button.dataset.speedLabel, nativeSpeed);
    }

    function getNearestSpeedButton(targetSpeed) {
      if (!speedButtons.length) {
        return null;
      }

      return speedButtons.reduce((closest, button) => {
        const closestDifference = Math.abs(
          getDisplayedSpeed(closest) - targetSpeed
        );

        const currentDifference = Math.abs(
          getDisplayedSpeed(button) - targetSpeed
        );

        return currentDifference < closestDifference ? button : closest;
      }, speedButtons[0]);
    }

    function getActiveSpeedButton() {
      return (
        player.querySelector("[data-gt-video-speed-option].is-active") ||
        getNearestSpeedButton(defaultSpeed)
      );
    }

    function applySpeed(button) {
      if (!button) {
        return;
      }

      const displayedSpeed = getDisplayedSpeed(button);
      const playbackRate = clamp(displayedSpeed / nativeSpeed, 0.0625, 4);

      video.playbackRate = playbackRate;

      speedButtons.forEach((item) => {
        const active = item === button;

        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", active ? "true" : "false");
      });
    }

    function updatePlayerState() {
      const duration = Number(video.duration) || 0;
      const currentTime = Number(video.currentTime) || 0;
      const percent = duration > 0 ? (currentTime / duration) * 100 : 0;

      progress.max = duration || 0;
      progress.value = currentTime;
      progress.style.setProperty("--gt-player-progress", `${percent}%`);
      timeLabel.textContent = `${formatTime(currentTime)} / ${formatTime(duration)}`;

      player.classList.toggle("is-playing", !video.paused && !video.ended);
      player.classList.toggle("is-paused", video.paused || video.ended);
    }

    function togglePlayback() {
      if (video.paused || video.ended) {
        applySpeed(getActiveSpeedButton());

        video.play().catch(() => {
          player.classList.add("is-paused");
        });
      } else {
        video.pause();
      }
    }

    toggleButton?.addEventListener("click", togglePlayback);
    bigPlayButton?.addEventListener("click", togglePlayback);
    video.addEventListener("click", togglePlayback);

    replayButton?.addEventListener("click", () => {
      video.currentTime = 0;
      applySpeed(getActiveSpeedButton());

      video.play().catch(() => {
        player.classList.add("is-paused");
      });
    });

    fullscreenButton?.addEventListener("click", () => {
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        player.requestFullscreen?.();
      }
    });

    progress?.addEventListener("input", () => {
      const value = Number(progress.value);

      if (Number.isFinite(value)) {
        video.currentTime = value;
        updatePlayerState();
      }
    });

    speedButtons.forEach((button) => {
      button.addEventListener("click", () => {
        applySpeed(button);
      });
    });

    video.addEventListener("loadedmetadata", () => {
      applySpeed(getNearestSpeedButton(defaultSpeed));
      updatePlayerState();
    });

    video.addEventListener("timeupdate", updatePlayerState);
    video.addEventListener("play", updatePlayerState);
    video.addEventListener("pause", updatePlayerState);
    video.addEventListener("ended", updatePlayerState);

    applySpeed(getNearestSpeedButton(defaultSpeed));
    updatePlayerState();
  }

  function initializeSection(section) {
    if (!section || section.dataset.gtScenarioInitialized === "true") {
      return;
    }

    section.dataset.gtScenarioInitialized = "true";

    initializeNumberFormats(section);
    initializeScenarioNavigation(section);
    initializeDistributionTooltips(section);

    section
      .querySelectorAll("[data-gt-scenario-player]")
      .forEach((player) => initializePlayer(player));
  }

  function bootstrap(scope = document) {
    scope
      .querySelectorAll("[data-gt-scenario-section]")
      .forEach((section) => initializeSection(section));
  }

  window.KalmixGroundTruthScenarioResults = {
    bootstrap
  };

  document.addEventListener("DOMContentLoaded", () => {
    bootstrap();
  });

  document.addEventListener("shopify:section:load", (event) => {
    bootstrap(event.target);
  });
})();